import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTheme } from '../../context/useTheme';
import { useAuth } from '../../contexts/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { GLOBAL_SEARCH_LABEL, GLOBAL_SEARCH_PLACEHOLDER, useGlobalSearch } from '../../hooks/useGlobalSearch';
import NotificationsPopover from './NotificationsPopover';
import BusinessMenu from './BusinessMenu';

/**
 * The prototype's topbar: 58px at `md:` and up — and below `md:`, not a bar at all.
 *
 * On a phone this is the first ROW of the design's single green block: logo left, round icon
 * buttons right, no border and no fixed height, with `PageHeading`'s `mobileBand` continuing the
 * same green directly underneath. The user's report was that "the notification bar shouldn't exist
 * at all" on mobile — it was a third strip sandwiching the amber account banners between two
 * greens. The banners moved to a fixed strip above the tab bar (see `RedesignLayout`) and the bar
 * stopped being a bar.
 *
 * The prototype's search field is a decorative <div> with placeholder text; it is a real search
 * input here with a label, because the mockup's version cannot be typed into or focused.
 *
 * That field is **desktop only**. The design's phone bar carries the logo instead — there was
 * never room on a phone for a mark AND a search box, and the design resolves it by not having
 * both: search lives in the dashboard's green band (`MobileSearchField`) and on /search. Both
 * fields share `useGlobalSearch`, so they submit, trim and gate identically.
 */
export default function RedesignHeader() {
    const { toggleTheme } = useTheme();
    const { activeBusiness } = useAuth();
    const { canSearch, searchTerm, setSearchTerm, submitSearch } = useGlobalSearch();
    // Owned here, once, rather than inside NotificationsPopover — the badge needs unreadCount
    // regardless of whether the popover is open, and a second hook instance inside the popover
    // would mean two independent polls (and two copies of state that could drift) instead of one.
    const { notifications, unreadCount, refresh, markAllRead, openNotification } = useNotifications();
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    // Opening is a fresh look, not the last poll's — refetch then (and only then; closing needs nothing).
    const toggleNotifications = () => {
        if (!notificationsOpen) void refresh();
        setNotificationsOpen(!notificationsOpen);
    };
    // Wraps the bell *and* the popover (not just the popover) — the same trick legacy
    // NotificationIcon's single dropdownRef uses. Without it, a click on the bell itself is seen
    // as "outside" by the popover's own outside-click check, which closes it a beat before the
    // bell's own onClick re-opens it — net effect, the bell can never close an open popover.
    const notificationsContainerRef = useRef<HTMLDivElement>(null);
    const [businessMenuOpen, setBusinessMenuOpen] = useState(false);
    // Same shared-container trick as the bell above, for the same reason — this ref wraps the chip
    // *and* the menu it opens, so the chip's own click isn't mistaken for an outside click.
    const businessMenuContainerRef = useRef<HTMLDivElement>(null);

    return (
        // Three columns at `md:` and up — `1fr auto 1fr` — so the business chip sits in the middle
        // one and the two flanks are forced to equal width. That is what makes the chip centred on
        // the BAR rather than in the leftover gap between the search field and the icons, which are
        // very different widths. Below `md:` the chip is hidden and this is an ordinary flex row.
        <div
            data-testid="redesign-header"
            // Not sticky itself: RedesignLayout pins this and the account banners together as one
            // block, so the banners cannot overlap it and the whole stack has one measurable
            // height. See its [data-sticky-chrome] wrapper.
            /* Dark green below `md:`, white from `md:` up. The design's phone screens open with a
               green band that runs from the status bar down through the page title, and this row is
               its top slice — PageHeading's `mobileBand` draws the rest, flush underneath. Every
               colour below is stated as a mobile value with an `md:` counterpart putting the
               desktop bar back exactly as it was.

               Three things are stated ONLY at `md:` because mobile must not have them at all:
               `md:border-b` (Tailwind's preflight zeroes every border width, so omitting the base
               utility leaves no line for the green band to be interrupted by), `md:h-[58px]` (a
               phone row sizes to the logo, and a fixed height is what made this look like a
               separate bar), and `md:py-0` cancelling the `pt-3.5` that gives the design's 14px of
               green above the logo. `px-5` matches `mobileBand`'s own `px-5`, so the logo and the
               page title below it share one left edge. */
            className="flex justify-between items-center md:grid md:grid-cols-[1fr_auto_1fr] bg-sidebar md:bg-surface md:border-b md:border-border md:h-[58px] px-5 md:px-9 py-3.5 md:py-0"
        >
            {/* The phone bar's mark, where the design puts it, in the slot the search field used
                to occupy. `md:hidden` is doing more work than it looks: a display:none child is
                not a grid item at all, so from `md:` up this leaves the grid with exactly the same
                three children it has always had (the form, the chip, the icons) and the chip keeps
                its centring. Same asset RedesignSidebar reverses out over the same green. */}
            {/* A link to the public home page. The LINK carries `md:hidden` now, not the image —
                it is the grid item, so it is what has to leave the grid from `md:` up. */}
            <Link to="/" aria-label="DAconnect home" className="block flex-none md:hidden">
                <img
                    src="/images/logo/DA-Connect_logo_title_transparent_dark_logo.svg"
                    alt=""
                    /* 44px, the height the design draws it at. It was `h-8` only because the old
                       52px bar could not fit more; with the fixed height gone the row sizes to the
                       mark instead. */
                    className="block h-11 w-auto"
                />
            </Link>

            {/* The form, not the input, is the header's first column — it carries the sizing the
                input used to, so wrapping the input changed nothing about the layout and the grid
                still has exactly three children (this, the chip, the icons).

                Hidden below `md:`: on a phone the mark above stands here instead, and the field
                itself is on the dashboard and on /search. Every style on it is therefore a plain
                desktop value again — it has no mobile appearance left to state. */}
            {canSearch ? (
            <form
                role="search"
                onSubmit={submitSearch}
                // min-width:0 because a grid item will not shrink below its own content width
                // without it — the field would refuse to narrow and push the chip off centre.
                className="hidden md:block min-w-0 md:mr-6 md:max-w-[420px]"
            >
                <label className="sr-only" htmlFor="global-search">
                    {GLOBAL_SEARCH_LABEL}
                </label>
                <input
                    id="global-search"
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={GLOBAL_SEARCH_PLACEHOLDER}
                    className="w-full min-w-0 bg-canvas border border-border rounded-[10px] px-3.5 py-[9px] text-[13.5px] text-ink placeholder:text-ink-faint"
                />
            </form>
            ) : (
                /* An empty first column, not nothing. The grid centres the business chip by giving
                   its two flanks equal `1fr` width, which needs three children — drop to two and
                   the chip slides into this slot instead of staying centred on the bar. Hidden
                   below `md:` for the same reason the form is: the flex row there is the logo and
                   the icons, and an empty box between them is not wanted. */
                <div aria-hidden className="hidden md:block" />
            )}

            {/* The grid's middle column. In flow rather than absolutely positioned, so it cannot
                overlap the search field or the icons however wide they get — the columns simply
                share the row. `relative` anchors the menu that hangs below it. The width cap and
                truncation stay: a long business name would otherwise widen this column and squeeze
                both flanks. Hidden below `md:` — mobile reaches the same menu through the More
                sheet instead. */}
            <div
                ref={businessMenuContainerRef}
                className="relative hidden md:flex md:justify-center"
            >
                <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={businessMenuOpen}
                    onClick={() => setBusinessMenuOpen((open) => !open)}
                    className="flex items-center gap-[9px] max-w-[200px] px-3.5 py-1.5 border border-border rounded-full bg-surface"
                >
                    <span className="size-[22px] rounded-md bg-sidebar text-white grid place-items-center text-[10px] font-bold flex-none">
                        {activeBusiness?.name?.[0] ?? '?'}
                    </span>
                    <span className="min-w-0 truncate text-[13px] font-bold text-ink">{activeBusiness?.name}</span>
                    <span className="text-[10px] text-ink-faint flex-none" aria-hidden="true">▾</span>
                </button>

                <BusinessMenu
                    open={businessMenuOpen}
                    onClose={() => setBusinessMenuOpen(false)}
                    boundaryRef={businessMenuContainerRef}
                />
            </div>

            {/* gap-2 is the design's 8px between the two round buttons; `md:gap-1.5` puts the
                desktop bar's tighter spacing back. */}
            <div className="flex items-center gap-2 md:gap-1.5 md:justify-self-end">
                <button
                    type="button"
                    title="Light / dark mode"
                    aria-label="Light / dark mode"
                    onClick={toggleTheme}
                    /* 36px round button on the translucent white fill the design gives both icons;
                       `md:bg-transparent` is the desktop bar's bare icon back again. */
                    className="size-9 rounded-full grid place-items-center bg-white/12 md:bg-transparent text-white md:text-icon"
                >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                    </svg>
                </button>
                <div ref={notificationsContainerRef} className="relative">
                    <button
                        type="button"
                        // The exact count belongs in the name: the badge is visual and capped at 9+.
                        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                        aria-haspopup="dialog"
                        aria-expanded={notificationsOpen}
                        onClick={toggleNotifications}
                        /* Same round translucent button as the theme toggle above. */
                        className="relative size-9 rounded-full grid place-items-center bg-white/12 md:bg-transparent text-white md:text-icon"
                    >
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        </svg>
                        {unreadCount > 0 && (
                            <span
                                data-testid="notification-count"
                                // Hidden from assistive tech: the button's name already says it.
                                aria-hidden="true"
                                /* Pale pill on the green row, leaf on the white bar: `bg-leaf`
                                   over `bg-sidebar` is green on green, which is the one place the
                                   badge has to be legible. Same pair the design draws.

                                   Now that the bell is a filled circle the badge hangs just
                                   OUTSIDE it, at the design's -2px/-2px, instead of sitting inside
                                   the fill; `md:top-0.5 md:right-0` is the unfilled desktop
                                   button's placement, unchanged. */
                                className="absolute -top-0.5 -right-0.5 md:top-0.5 md:right-0 bg-leaf-pill text-sidebar md:bg-leaf md:text-white text-[9.5px] font-bold rounded-full px-1.5 py-px"
                            >
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </button>

                    <NotificationsPopover
                        open={notificationsOpen}
                        onClose={() => setNotificationsOpen(false)}
                        notifications={notifications}
                        onMarkAllRead={markAllRead}
                        onOpenNotification={openNotification}
                        boundaryRef={notificationsContainerRef}
                    />
                </div>
            </div>
        </div>
    );
}
